import { FC, useContext, useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router';
import { useTranslation } from '../i18n';
import { AppContext } from '../window';
import { NotificationContext } from '../context/NotificationContext';

interface MemberRouteProps {
  children: React.ReactNode;
}

const isProfilePath = (pathname: string): boolean => /^\/user\/\d+/.test(pathname);

/**
 * Home with the modal a guest on this member route gets: profiles (`/user/<id>`,
 * where pretty profile URLs lead) the signup modal, every other member route
 * the login modal; `returnUrl` brings the visitor back here afterwards.
 */
export const guestEntryPath = (pathname: string, search: string): string => {
  const modal = isProfilePath(pathname) ? 'signup' : 'login';
  return `/?${modal}=1&returnUrl=${encodeURIComponent(pathname + search)}`;
};

const MemberRoute: FC<MemberRouteProps> = ({ children }) => {
  const { t } = useTranslation();
  const { user } = useContext(AppContext);
  const { addNotification } = useContext(NotificationContext);
  const location = useLocation();
  // performLogout() broadcasts `app:logout` before clearing the user: a
  // deliberate logout goes home without the toast or the login modal
  const loggedOut = useRef(false);

  useEffect(() => {
    const onLogout = () => { loggedOut.current = true; };
    window.addEventListener('app:logout', onLogout);
    return () => window.removeEventListener('app:logout', onLogout);
  }, []);

  useEffect(() => {
    // The signup modal a profile link opens speaks for itself
    if (!user && location.pathname !== '/' && !loggedOut.current && !isProfilePath(location.pathname)) {
      addNotification({
        level: 'warning',
        message: t('general.please_login_to_access')
      });
    }
  }, [user, location.pathname]); // Remove addNotification from deps to prevent re-runs

  if (!user) {
    if (loggedOut.current) {
      return <Navigate to="/" replace />;
    }
    return <Navigate to={guestEntryPath(location.pathname, location.search)} replace />;
  }

  return <>{children}</>;
};

export default MemberRoute;
